import { api } from "../../../services/apiClient";
import type {
  TimeEfficiencyFilters,
  TimeEfficiencyKpis,
  TimeEfficiencyMonthlyEvolutionEntry,
  TimeEfficiencyOptions,
  TimeEfficiencyProjectHistory,
  TimeEfficiencyProjectItem,
  TimeEfficiencyProjectsResponse,
  TimeEfficiencyRankings,
  TimeEfficiencyReopenKpi,
  TimeEfficiencyStageChartEntry,
} from "../types/timeEfficiency.types";

/** GET /dashboard/time-efficiency/options — células, coordenadores, tipos de evento, status. */
export async function getTimeEfficiencyOptions(): Promise<TimeEfficiencyOptions> {
  const { data } = await api.get<TimeEfficiencyOptions>(
    "/dashboard/time-efficiency/options"
  );
  return data;
}

/**
 * GET /dashboard/time-efficiency/projects — tabela paginada de projetos em andamento.
 *
 * Nota (#MMV-2615, Fase 1): `filters.status = []` (usuário clicou "Limpar"
 * dentro do painel de status, querendo ver zero projetos) e
 * `filters.status = undefined` (nenhum filtro, ver todos) devem se
 * comportar diferente, mas o axios por padrão não envia um array vazio na
 * query string -- os dois casos chegam iguais na Api hoje. Resolver isso
 * (serializer customizado ou um sentinela explícito) fica pra quando a
 * regra fina do filtro de status for revisada.
 */
export async function getTimeEfficiencyProjects(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyProjectsResponse> {
  const { data } = await api.get<TimeEfficiencyProjectsResponse>(
    "/dashboard/time-efficiency/projects",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/projects-above-average — concluídos com
 * duração total acima da média do conjunto filtrado (drill-down do KPI
 * "Projetos acima da média"). */
export async function getTimeEfficiencyProjectsAboveAverage(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyProjectsResponse> {
  const { data } = await api.get<TimeEfficiencyProjectsResponse>(
    "/dashboard/time-efficiency/projects-above-average",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/kpis — calculado só sobre projetos concluídos. */
export async function getTimeEfficiencyKpis(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyKpis> {
  const { data } = await api.get<TimeEfficiencyKpis>(
    "/dashboard/time-efficiency/kpis",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/stage-chart — tempo médio por etapa (concluídos x em andamento). */
export async function getTimeEfficiencyStageChart(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyStageChartEntry[]> {
  const { data } = await api.get<TimeEfficiencyStageChartEntry[]>(
    "/dashboard/time-efficiency/stage-chart",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/stale-projects — top projetos parados há mais tempo. */
export async function getTimeEfficiencyStaleProjects(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyProjectItem[]> {
  const { data } = await api.get<TimeEfficiencyProjectItem[]>(
    "/dashboard/time-efficiency/stale-projects",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/monthly-evolution — média de dias por mês
 * de início do projeto (mês atual + 5 anteriores), sem filtro. */
export async function getTimeEfficiencyMonthlyEvolution(): Promise<
  TimeEfficiencyMonthlyEvolutionEntry[]
> {
  const { data } = await api.get<TimeEfficiencyMonthlyEvolutionEntry[]>(
    "/dashboard/time-efficiency/monthly-evolution"
  );
  return data;
}

/** GET /dashboard/time-efficiency/reopen-kpi — quantos projetos (concluídos
 * + em andamento) voltaram a um status que já tinham passado. */
export async function getTimeEfficiencyReopenKpi(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyReopenKpi> {
  const { data } = await api.get<TimeEfficiencyReopenKpi>(
    "/dashboard/time-efficiency/reopen-kpi",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/projects-with-reopening — drill-down do
 * KPI "Projetos com reabertura". */
export async function getTimeEfficiencyProjectsWithReopening(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyProjectsResponse> {
  const { data } = await api.get<TimeEfficiencyProjectsResponse>(
    "/dashboard/time-efficiency/projects-with-reopening",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/rankings — "Tempo médio total por
 * célula"/"Ranking por coordenador"/"Ranking por tipo de evento", mesma
 * fonte de dado que o relatório já exportado do sistema. */
export async function getTimeEfficiencyRankings(
  filters: TimeEfficiencyFilters
): Promise<TimeEfficiencyRankings> {
  const { data } = await api.get<TimeEfficiencyRankings>(
    "/dashboard/time-efficiency/rankings",
    { params: filters }
  );
  return data;
}

/** GET /dashboard/time-efficiency/projects/:projectId/history — histórico
 * completo de status de um projeto (modal "Histórico de status" aberto a
 * partir do número na coluna Reaberturas da tabela). */
export async function getTimeEfficiencyProjectHistory(
  projectId: string
): Promise<TimeEfficiencyProjectHistory> {
  const { data } = await api.get<TimeEfficiencyProjectHistory>(
    `/dashboard/time-efficiency/projects/${projectId}/history`
  );
  return data;
}
